import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRealtime, useSocketEvent } from './realtime';
import { useToast } from './toast';
import { connectToSfu, type SfuSession } from './sfu';

/**
 * Live announcements for an open space: stand on its Announcement Circle and
 * everyone in that space hears you, walk off and the channel is free again.
 * See services/announce.service.ts for how this widens FR-7.2.
 *
 * It connects only while somebody is actually standing there. An open floor
 * holding a hundred live audio connections all day would cost a fortune to
 * carry silence, and a channel that is quiet most of the time should be quiet
 * on the wire too.
 *
 * One person is audible because the server issues everyone else a credential
 * that cannot publish — not because every other browser was asked to behave.
 */

interface Speaker {
  userId: string;
  name: string;
}

interface PushToTalkValue {
  /** Who is on the channel right now, us included. */
  speaker: Speaker | null;
  /** True while we are the one holding it. */
  holding: boolean;
  /** Between pressing and being live — a walkie-talkie's beep. */
  opening: boolean;
  take: () => Promise<void>;
  release: () => void;
}

const PushToTalkContext = createContext<PushToTalkValue>({
  speaker: null,
  holding: false,
  opening: false,
  take: async () => undefined,
  release: () => undefined,
});

export function PushToTalkProvider({ children }: { children: ReactNode }) {
  const { socket } = useRealtime();
  const { push } = useToast();

  const [speaker, setSpeaker] = useState<Speaker | null>(null);
  const [holding, setHolding] = useState(false);
  const [opening, setOpening] = useState(false);

  const sessionRef = useRef<SfuSession | null>(null);
  const holdingRef = useRef(false);
  const socketRef = useRef(socket);
  socketRef.current = socket;
  const pushRef = useRef(push);
  pushRef.current = push;

  const closeSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) void session.disconnect().catch(() => undefined);
  }, []);

  const take = useCallback(async () => {
    const client = socketRef.current;
    if (!client || holdingRef.current) return;
    holdingRef.current = true;
    setOpening(true);

    const result = await new Promise<{
      ok: boolean;
      error?: string;
      serverUrl?: string;
      token?: string;
    }>((resolve) => client.emit('announce:take', {}, resolve));

    if (!result.ok || !result.serverUrl || !result.token) {
      holdingRef.current = false;
      setOpening(false);
      pushRef.current({
        kind: 'info',
        title: 'Cannot speak right now',
        body: result.error ?? 'The channel is busy.',
      });
      return;
    }

    try {
      sessionRef.current = await connectToSfu(
        { serverUrl: result.serverUrl, token: result.token, microphone: true, camera: false },
        {
          onLocalStream: () => undefined,
          onRemoteStreams: () => undefined,
          onConnected: () => setOpening(false),
          onClosed: () => undefined,
        },
      );
      // Let go while we were connecting: do not start talking into a room
      // nobody thinks we are in.
      if (!holdingRef.current) {
        closeSession();
        return;
      }
      setHolding(true);
      setOpening(false);
    } catch {
      holdingRef.current = false;
      setOpening(false);
      client.emit('announce:release', {});
      pushRef.current({
        kind: 'error',
        title: 'Could not start speaking',
        body: 'HomeWork needs microphone permission to announce.',
      });
    }
  }, [closeSession]);

  const release = useCallback(() => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    setHolding(false);
    setOpening(false);
    closeSession();
    socketRef.current?.emit('announce:release', {});
  }, [closeSession]);

  useSocketEvent('announce:started', ({ speaker: who, listen }) => {
    setSpeaker(who);
    // Our own hold comes back to us as well; we are already publishing.
    if (holdingRef.current || !listen) return;
    void (async () => {
      try {
        closeSession();
        sessionRef.current = await connectToSfu(
          { serverUrl: listen.serverUrl, token: listen.token, microphone: false, camera: false },
          {
            onLocalStream: () => undefined,
            // Nothing to render: the announcer's voice plays through the
            // element the SDK attaches for it.
            onRemoteStreams: () => undefined,
            onAudioBlocked: () => {
              pushRef.current({
                kind: 'info',
                title: `${who.name} is announcing`,
                body: 'Your browser is holding audio until you interact with the page.',
                action: {
                  label: 'Play audio',
                  onClick: () => void sessionRef.current?.startAudio().catch(() => undefined),
                },
              });
            },
            onConnected: () => undefined,
            onClosed: () => undefined,
          },
        );
      } catch {
        // Nothing useful to say: the name still pulses, there is just no audio.
      }
    })();
  });

  useSocketEvent('announce:ended', () => {
    setSpeaker(null);
    if (holdingRef.current) {
      holdingRef.current = false;
      setHolding(false);
    }
    closeSession();
  });

  // A tab going away mid-sentence should not hold the floor.
  useEffect(() => {
    const drop = (): void => release();
    window.addEventListener('pagehide', drop);
    window.addEventListener('beforeunload', drop);
    return () => {
      window.removeEventListener('pagehide', drop);
      window.removeEventListener('beforeunload', drop);
      release();
    };
  }, [release]);

  const value = useMemo<PushToTalkValue>(
    () => ({ speaker, holding, opening, take, release }),
    [speaker, holding, opening, take, release],
  );

  return <PushToTalkContext.Provider value={value}>{children}</PushToTalkContext.Provider>;
}

export function usePushToTalk(): PushToTalkValue {
  return useContext(PushToTalkContext);
}
